import type {
  Fetcher,
  RequestInterceptor,
  ResponseProcessor,
} from '@/infrastructure/http/HttpClient.types'
import type {
  HttpClient,
  RequestConfig,
} from '@/infrastructure/interfaces/HttpClient'

export class ApiClient implements HttpClient {
  constructor(
    private readonly apiDomain: string,
    private readonly fetcher: Fetcher,
    private readonly interceptors: RequestInterceptor[],
    private readonly processor: ResponseProcessor,
  ) {}

  async request<T = unknown>(config: RequestConfig): Promise<T> {
    // 1. 执行拦截器链 (顺序执行)
    let finalConfig = config
    for (const interceptor of this.interceptors) {
      finalConfig = await interceptor.intercept(finalConfig)
    }

    // 2. 构建最终 URL (处理斜线和查询参数)
    const url = this.buildUrl(finalConfig)

    // 3. 处理请求体 - 对于特殊对象不要进行 JSON 序列化
    let requestBody: BodyInit | undefined
    if (finalConfig.body) {
      // 检查是否为特殊对象类型，这些类型不应被 JSON.stringify
      if (
        finalConfig.body instanceof FormData ||
        finalConfig.body instanceof Blob ||
        finalConfig.body instanceof ArrayBuffer ||
        ArrayBuffer.isView(finalConfig.body) ||
        finalConfig.body instanceof URLSearchParams ||
        finalConfig.body instanceof ReadableStream
      ) {
        requestBody = finalConfig.body as BodyInit
      } else {
        // 对于普通对象或数组，进行 JSON 序列化
        requestBody = JSON.stringify(finalConfig.body)
      }
    }

    // 3. 执行请求
    const response = await this.fetcher(url, {
      method: finalConfig.method || 'GET',
      headers: finalConfig.headers as Record<string, string>,
      body: requestBody,
    })

    // 4. 委托给处理器解析响应
    return this.processor.process<T>(response)
  }

  private buildUrl(config: RequestConfig): string {
    const { path, params } = config
    let urlPath = path

    // 1. 处理查询参数 (Params)
    if (params) {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        urlPath += (urlPath.includes('?') ? '&' : '?') + queryString
      }
    }

    // 2. 拼接 Domain 并清理重复斜线
    return `${this.apiDomain}${urlPath}`.replace(/([^:]\/)\/+/g, '$1')
  }

  // 辅助方法，保持与旧版代码的兼容性
  get<T>(
    path: string,
    options?: {
      query?: Record<string, string | number | boolean | null | undefined>
      headers?: Record<string, string>
    },
  ): Promise<T> {
    return this.request<T>({
      path,
      method: 'GET',
      params: options?.query,
      headers: options?.headers,
    })
  }

  post<T>(
    path: string,
    options?: {
      body?: unknown
      headers?: Record<string, string>
      query?: Record<string, string | number | boolean | null | undefined>
    },
  ): Promise<T> {
    return this.request<T>({
      path,
      method: 'POST',
      body: options?.body,
      headers: options?.headers,
      params: options?.query,
    })
  }

  put<T>(
    path: string,
    options?: {
      body?: unknown
      headers?: Record<string, string>
      query?: Record<string, string | number | boolean | null | undefined>
    },
  ): Promise<T> {
    return this.request<T>({
      path,
      method: 'PUT',
      body: options?.body,
      headers: options?.headers,
      params: options?.query,
    })
  }

  delete<T>(
    path: string,
    options?: {
      query?: Record<string, string | number | boolean | null | undefined>
      headers?: Record<string, string>
    },
  ): Promise<T> {
    return this.request<T>({
      path,
      method: 'DELETE',
      params: options?.query,
      headers: options?.headers,
    })
  }
}
